import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {testIdProps} from '@github-ui/test-id-props'
import {TableIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useMemo} from 'react'

import {OpenClosedProjects} from './OpenClosedProjects'
import type {ProjectSortValue} from './project-sort-options'
import {ProjectRow} from './ProjectRow'
import {ProjectSortMenu} from './ProjectSortMenu'
import styles from './RepositoryProjects.module.css'
import type {Project} from './types'

interface ProjectListProps {
  projects: Project[]
  openCount: number
  closedCount: number
  hasNextPage: boolean
  loadingMore: boolean
  onLoadMore: () => void
  viewerCanPush: boolean
  ownerLogin: string
  ownerIsOrganization: boolean
  query: string
  sort: ProjectSortValue
  onSortChange: (value: ProjectSortValue) => void
  isRefetching?: boolean
}

export function ProjectList({
  projects,
  openCount,
  closedCount,
  hasNextPage,
  loadingMore,
  onLoadMore,
  viewerCanPush,
  ownerLogin,
  ownerIsOrganization,
  query,
  sort,
  onSortChange,
  isRefetching = false,
}: ProjectListProps) {
  const newProjectUrl = ownerIsOrganization ? `/orgs/${ownerLogin}/projects` : `/users/${ownerLogin}/projects`
  const listItemsHeader = (
    <ListViewMetadata sectionFilters={<OpenClosedProjects openCount={openCount} closedCount={closedCount} />}>
      <ProjectSortMenu sort={sort} onSortChange={onSortChange} />
    </ListViewMetadata>
  )

  const loadMoreButton = useMemo(() => {
    return hasNextPage ? (
      <div className={styles.loadMoreButtonWrapper}>
        <Button
          variant="invisible"
          onClick={onLoadMore}
          className={styles.loadMoreButton}
          data-testid="load-more-projects-button"
          loading={loadingMore}
        >
          Load more projects
        </Button>
      </div>
    ) : null
  }, [hasNextPage, loadingMore, onLoadMore])

  const renderProjectList = () => {
    if (projects.length > 0) {
      return projects.map(project => <ProjectRow key={project.id} project={project} />)
    }

    const hasActiveQuery = query.trim().length > 0
    return hasActiveQuery ? (
      <NoMatchesBlankslate />
    ) : (
      <NoProjectsBlankslate viewerCanPush={viewerCanPush} newProjectUrl={newProjectUrl} />
    )
  }

  return (
    <>
      <div className={styles.projectListWrapper} data-hpc aria-busy={isRefetching}>
        <ListView
          {...testIdProps('repository-project-list-view')}
          title="Repository projects"
          metadata={listItemsHeader}
          titleHeaderTag="h2"
          singularUnits="project"
          pluralUnits="projects"
          as={projects.length > 0 ? 'ul' : 'div'}
          role={projects.length > 0 ? 'list' : 'status'}
        >
          {renderProjectList()}
        </ListView>
      </div>
      {loadMoreButton}
    </>
  )
}

function NoMatchesBlankslate() {
  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <TableIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>No projects matched your search</Blankslate.Heading>
        <Blankslate.Description>
          Try a different search term or clear the search to see all projects.
        </Blankslate.Description>
      </Blankslate>
    </div>
  )
}

interface NoProjectsBlankslateProps {
  viewerCanPush: boolean
  newProjectUrl: string
}

function NoProjectsBlankslate({viewerCanPush, newProjectUrl}: NoProjectsBlankslateProps) {
  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <TableIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>No projects found</Blankslate.Heading>
        <Blankslate.Description>
          {viewerCanPush
            ? 'There are no projects linked to this repository yet.'
            : 'There are no projects linked to this repository.'}
        </Blankslate.Description>
        {viewerCanPush && <Blankslate.PrimaryAction href={newProjectUrl}>New project</Blankslate.PrimaryAction>}
      </Blankslate>
    </div>
  )
}
